import CAD from '@assets/images/country-flags/Canada.svg';
import USD from '@assets/images/country-flags/US.svg';
import { Currencies } from '@generated/graphql';
import {
  InputAdornment,
  TextField,
} from '@ovComponents/2-component/textField/textField';
import { getDecimalPlaceholder } from '@utils/common';
import { isCurrencySymbolAfter } from '@utils/moneyFormatting';
import { Box } from 'hephaestus/web';
import { t } from 'i18next';

type AmountFieldProps = {
  id?: string;
  name?: string;
  amount?: string | number;
  setAmount: (amount: string) => void;
  label?: string;
  onBlur?: any;
  error?: any;
  sx?: any;
  disabled?: boolean;
  locked?: boolean;
  infoTooltip?: string;
  errorText?: string;
  inputProps?: any;
  InputProps?: any;
  size?: any;
  fullWidth?: boolean;
  useFormatAmountValueCents?: boolean;
  testId?: string;
  decimalPlaces?: number;
  unlimitedDecimals?: boolean;
  placeholder?: string;
  currency?: string;
  preventNegative?: boolean;
};

export const AmountField = ({
  id,
  name,
  amount,
  setAmount,
  label,
  onBlur,
  error,
  sx,
  infoTooltip,
  disabled,
  locked,
  errorText,
  inputProps,
  InputProps,
  size,
  fullWidth = true,
  useFormatAmountValueCents = false,
  testId,
  decimalPlaces = 2,
  unlimitedDecimals = false,
  placeholder,
  currency,
  preventNegative = false,
}: AmountFieldProps) => {
  const symbolAfter = isCurrencySymbolAfter();
  const leadingIcon = symbolAfter ? undefined : 'dollar';
  const trailingIcon = symbolAfter ? 'dollar' : undefined;
  const parseValue = (value: unknown): any => {
    if (value === undefined || value === '') {
      return '';
    }

    if (!useFormatAmountValueCents) {
      return value;
    }

    const dollars = Number(value) / 100;
    if (Number.isNaN(dollars)) {
      return '';
    }

    // Use the natural string form (no forced trailing zeros). NumberFormat's
    // `decimalScale` already caps the fractional length on display; padding here
    // would force a trailing zero back into the controlled value and prevent
    // the user from backspacing through the decimal portion (see OVT-5667).
    return dollars.toString();
  };

  const getCurrencyImage = () => {
    switch (currency) {
      case Currencies.Cad:
        return CAD;
      case Currencies.Usd:
        return USD;
      default:
        return null;
    }
  };

  const currencyImage = getCurrencyImage();

  const handleChange = (e: any) => {
    let value = e.target.value;
    if (preventNegative && Number(value) < 0) {
      value = Math.abs(Number(value)).toString();
    }
    setAmount(value);
  };

  return (
    <TextField
      onChange={handleChange}
      id={id}
      name={name}
      testId={testId ?? 'amount-field'}
      label={label ?? t('components:amount')}
      fullWidth={fullWidth}
      lockMessage={t('pageConfiguration:notEditableMessage')}
      disabled={disabled}
      locked={locked}
      value={parseValue(amount)}
      decimalPlaces={decimalPlaces}
      unlimitedDecimals={unlimitedDecimals}
      placeholder={placeholder ?? getDecimalPlaceholder(decimalPlaces)}
      type='dollar'
      leadingIcon={symbolAfter ? undefined : leadingIcon}
      trailingIcon={symbolAfter ? trailingIcon : undefined}
      onBlur={onBlur}
      error={error}
      sx={sx}
      infoTooltip={infoTooltip}
      errorText={errorText}
      inputProps={inputProps}
      size={size}
      InputProps={
        currency
          ? {
              ...InputProps,
              startAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {currencyImage && (
                    <Box
                      sx={{
                        height: '20px',
                        width: '20px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignContent: 'center',
                        borderRadius: 10,
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        alt={currency || 'currency'}
                        src={currencyImage}
                        style={{ objectFit: 'cover', height: '100%' }}
                      />
                    </Box>
                  )}
                  {!symbolAfter && <span>$</span>}
                </Box>
              ),
              endAdornment: symbolAfter ? (
                <InputAdornment position='end'>$</InputAdornment>
              ) : (
                InputProps?.endAdornment
              ),
            }
          : InputProps
      }
    />
  );
};
